// The shared analytics range (spec/dashboard.md sections 8.5 and 11.2; M7 slice 7): one date range per user — a preset of
// 7 / 14 / 30 / 90 days ending today, or a custom from–to of at most 92 days — and the "include testers" choice, persisted
// in localStorage["hue.analytics.range"] (a map by the signed-in e-mail) and shared by every analytics read of the pages
// (the calendar's metrics, the card badges, the store and segment series, the A/B results, the Export dialog's default).
import { useCallback, useEffect, useState } from 'react';
import type { Environment } from '../api/types';
import { loadSession } from '../auth/session';

const KEY = 'hue.analytics.range';
const DAY = 86400;
export const PRESETS = [7, 14, 30, 90] as const;
export const MAX_RANGE_DAYS = 92;

export type RangePreset = (typeof PRESETS)[number] | 'custom';

export interface StoredRange {
  preset: RangePreset;
  from?: string;
  to?: string;
  includeTesters?: boolean;
}

export interface AnalyticsRange {
  preset: RangePreset;
  /** game-time days, inclusive (`YYYY-MM-DD`) */
  from: string;
  to: string;
  days: number;
  includeTesters: boolean;
}

const DEFAULT: StoredRange = { preset: 30 };

function userKey(): string {
  return loadSession()?.email ?? '';
}

export function readRange(): StoredRange {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return memory ?? DEFAULT;
    const map = JSON.parse(raw) as Record<string, StoredRange>;
    const stored = map[userKey()];
    if (!stored) return DEFAULT;
    if (stored.preset === 'custom' ? !rangeError(stored.from ?? '', stored.to ?? '') : PRESETS.includes(stored.preset as never)) return stored;
    return { ...DEFAULT, includeTesters: stored.includeTesters };
  } catch {
    return memory ?? DEFAULT;
  }
}

function writeRange(next: StoredRange): void {
  try {
    const raw = localStorage.getItem(KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, StoredRange>) : {};
    map[userKey()] = next;
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // a private window: the choice lives for the session only
    memory = next;
  }
}

let memory: StoredRange | null = null;
const listeners = new Set<() => void>();

/** `YYYY-MM-DD` of a unix instant in UTC. */
function utcDay(seconds: number): string {
  return new Date(seconds * 1000).toISOString().slice(0, 10);
}

/** The number of days of an inclusive range, or NaN for a malformed one. */
export function daysOf(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || !/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return NaN;
  return Math.round((b - a) / (DAY * 1000)) + 1;
}

/** Why a custom range is refused, or null (the API's limit: at most 92 days, from ≤ to). */
export function rangeError(from: string, to: string): string | null {
  const n = daysOf(from, to);
  if (Number.isNaN(n)) return 'Pick both days.';
  if (n < 1) return 'The range ends before it starts.';
  if (n > MAX_RANGE_DAYS) return `At most ${MAX_RANGE_DAYS} days.`;
  return null;
}

/** Today's game-time day of an environment: the UTC date of now + its offset (an environment on local time counts 0). */
export function gameToday(env: Pick<Environment, 'gameTimeOffsetSeconds' | 'useLocalTime'> | null | undefined, now = Date.now()): string {
  const offset = env && !env.useLocalTime ? Number(env.gameTimeOffsetSeconds ?? 0) : 0;
  return utcDay(Math.floor(now / 1000) + offset);
}

/** `from` .. `to` of a stored range, a preset ending today. */
export function resolveRange(stored: StoredRange, today: string): AnalyticsRange {
  const includeTesters = !!stored.includeTesters;
  if (stored.preset === 'custom' && stored.from && stored.to && !rangeError(stored.from, stored.to)) {
    return { preset: 'custom', from: stored.from, to: stored.to, days: daysOf(stored.from, stored.to), includeTesters };
  }
  const n = stored.preset === 'custom' ? 30 : stored.preset;
  const end = Date.parse(`${today}T00:00:00Z`) / 1000;
  return { preset: stored.preset === 'custom' ? 30 : stored.preset, from: utcDay(end - (n - 1) * DAY), to: today, days: n, includeTesters };
}

/** The shared range of the signed-in user, resolved against the environment's game-time today. */
export function useAnalyticsRange(env?: Pick<Environment, 'gameTimeOffsetSeconds' | 'useLocalTime'> | null): {
  range: AnalyticsRange;
  setPreset: (preset: (typeof PRESETS)[number]) => void;
  /** null when stored; else why the range is refused (nothing stored) */
  setCustom: (from: string, to: string) => string | null;
  setIncludeTesters: (on: boolean) => void;
} {
  const [stored, setStored] = useState<StoredRange>(() => readRange());
  useEffect(() => {
    const l = () => setStored(readRange());
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  const update = useCallback((next: StoredRange) => {
    writeRange(next);
    for (const l of Array.from(listeners)) l();
  }, []);
  const setPreset = useCallback((preset: (typeof PRESETS)[number]) => update({ preset, includeTesters: readRange().includeTesters }), [update]);
  const setCustom = useCallback(
    (from: string, to: string) => {
      const error = rangeError(from, to);
      if (!error) update({ preset: 'custom', from, to, includeTesters: readRange().includeTesters });
      return error;
    },
    [update],
  );
  const setIncludeTesters = useCallback((on: boolean) => update({ ...readRange(), includeTesters: on }), [update]);
  return { range: resolveRange(stored, gameToday(env)), setPreset, setCustom, setIncludeTesters };
}

/** Tests: forget the in-memory copy (localStorage is cleared between tests by the setup). */
export function resetAnalyticsRangeMemory(): void {
  memory = null;
}
